import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getQuestionTitlesByChartType } from 'src/components/Card/CardExporting/chartExporter/questionProcessor';
import { resetChartLegendsState } from 'src/managers/chartManager/stateManagement/legendHandler';
import ConfigurableChartThemeManager from 'src/managers/themeManager/inheritorThemeManager/ConfigurableChartThemeManager';
import { Card, MultipleCrossChart } from '../../../components';
import { annotationStrategyMap } from '../../../stores/actions/annotations';
import {
  fetchMultiCrossingChartData,
  fetchMultipleCrossChartColorConfigData
} from '../../../stores/actions/charts';
import {
  fetchMultCrossConfigs, removeMultCrossConfig
} from '../../../stores/actions/configs';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../util/formatters/stringNormalizer';
import ContextualCardAnnotations from '../../ContextualCardAnnotations';
import ContextualPopupExporting from '../../ContextualPopupExporting';
import { cardsMap } from '../cardsMap';
import { MULTIPLE_CARD } from '../cardTypes';
import { exportingTypes } from '../exportingTypes';
import * as Ui from '../styles';
import { graphTypes } from '../types';
import MultCrossConfig from './config/MultCrossConfig';

/**
 * filters a dataset using the data stored in the given
 * dataField that matches with the given keywords.
 */
// {dataset, datasetFilterField, query}
function filterByKeywords(context) {
  const { dataset, datasetFilterField, query } = context;
  if (!query) {
    return dataset;
  }
  if (query && query === '') {
    return dataset;
  }
  return dataset.filter((data) => removerCaracteresEspeciais(data[datasetFilterField])
    .toLowerCase()
    .includes(removerCaracteresEspeciais(query).toLowerCase()));
}

/**
 * extract title by profile features
 */
function extractTitleChart(profile) {
  const titleChartComplement = profile.caracteristicas.map(
    (caracteristica) => ({
      label: caracteristica.alternativa.conteudo,
      title: `Pergunta: ${caracteristica.pergunta.conteudo}`,
    }),
  );
  return titleChartComplement;
}

/**
 * @param data dados do cruzamento multiplo
 * @param isFullScreen flag indicando se o card do cruzamento multiplo deve ser exibido
 * em fullScreen ou não.
 * @param isSomeoneInFullScreen flag indicando se existe algum card em full screen.
 * @param onFullScreenToggle callback chamado quando existe uma interação com o botão toggle de fullscreen.
 */
function MultipleCrossChartCard(props) {
  // props from parent
  const {
    customerId,
    surveyId,
    questionId,
    questionTitle,
    isFullScreen,
    isSomeoneInFullScreen,
    onFullScreenToggle,
    theme,
  } = props;
  // state
  const [chartData, setChartData] = useState([]);
  const [withData, setWithData] = useState(false);
  const [presets, setPresets] = useState([
    /* {value, label} */
  ]);
  const [profileText, setProfileText] = useState(null);
  const [multipleCrossChartType, setMultipleCrossChartType] = useState('pie');
  const [filter, setFilter] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [selectedMultCrossConfigId, setSelectedMultCrossConfigId] = useState(null);
  const [editableConfigId, setEditableConfigId] = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showExporting, setShowExporting] = useState(false);

  // redux state
  const dispatch = useDispatch();

  const {
    configs: { multiCrossConfigs: configurationsState },
    charts: {
      multiCrossChartData: chartState,
    },
  } = useSelector((state) => state);

  // refs
  const cardRef = useRef();
  const chartRef = React.createRef();

  // Gerenciador de tema de cores e fontes
  const themeManager = new ConfigurableChartThemeManager(
    selectedMultCrossConfigId,
    surveyId,
    withData,
    chartRef,
    theme,
    multipleCrossChartType,
    MULTIPLE_CARD,
  );

  const { colorSchemeValue, fontsSizes, configurationError: chartConfigDataError, colorPickerColors } = themeManager.getConfig();

  useEffect(() => {
    if (chartConfigDataError) {
      toast.error(chartConfigDataError.toString());
    }
  }, [chartConfigDataError]);

  // resetPresets reinicializa os dados da configuração
  const resetPresets = useCallback(() => {
    setPresets([]);
    setSelectedMultCrossConfigId(null);
  }, []);

  // refreshConfigs atualiza a listagem de configurações
  const refreshConfigs = useCallback(() => {
    if (surveyId && customerId && questionId) {
      resetPresets();
      fetchMultCrossConfigs(customerId, surveyId, questionId, dispatch);
    }
  }, [surveyId, customerId, questionId, resetPresets, dispatch]);

  // resetData reinicializa os dados do gráfico
  const resetData = useCallback(() => {
    setChartData([]);
    setProfileText(null);
    setWithData(false);
    resetChartLegendsState(MULTIPLE_CARD);
  }, []);

  // gancho: quando a triade principal (customerId, surveyId, questionId) alterar
  // efeito: listar todas as configurações para a triade principal
  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  // gancho: alteração da listagem de configurações
  // efeito: definir com os dados da store
  useEffect(() => {
    // const configKey = `${customerId}_${surveyId}_${questionId}`;
    // const configList = configurationsState.payload && configurationsState.payload[configKey];
    const configList = configurationsState.payload;
    if (!configList) {
      resetPresets();
      return;
    }

    setPresets(
      configList.map((item) => ({
        label: item.descricao,
        value: item.id,
      })),
    );
  }, [configurationsState, resetPresets]);

  // gancho: alteração da listagem de configurações
  // efeito: selecionar o último item selecionado ou 0
  useEffect(() => {
    if (!isNullOrEmptyArray(presets) && cardRef.current) {
      const scheduledRefresh = () => {
        cardRef.current.selectPresetItemIndex(0);
      };
      setTimeout(scheduledRefresh, 0);
    }
  }, [presets, cardRef]);

  // gancho: alteração do erro
  // efeito: caso haja um erro, apresentar uma mensagem
  useEffect(() => {
    // get error
    const error = (chartState && chartState.error)
      || (configurationsState && configurationsState.error);
    // show error if exists
    if (error) {
      toast.error(error.toString());
    }
  }, [chartState, configurationsState]);

  // gancho: alteração na identidade da configuração
  // efeito: solicitar a listagem de dados do gráfico a partir do id da condiguração
  useEffect(() => {
    if (selectedMultCrossConfigId) {
      fetchMultiCrossingChartData(selectedMultCrossConfigId, dispatch);
    }
  }, [selectedMultCrossConfigId, dispatch]);

  // ao modificar o estado dos dados de plotagem
  useEffect(() => {
    // check if has data
    const crossing = chartState && chartState.payload && chartState.payload.perguntas_cruzadas;
    const profile = chartState && chartState.payload && chartState.payload.perfil;
    const hasData = !isNullOrEmptyArray(crossing) && profile;
    // set values
    if (hasData && selectedMultCrossConfigId) {
      setChartData(crossing);
      setProfileText(extractTitleChart(profile));

      const lastDataState = withData;
      const withDataState = true;
      // Garante que o fetch das configurações só será feito se o usuário tiver realmente trocado
      // os dados de plotagem.
      if (lastDataState !== withDataState) {
        fetchMultipleCrossChartColorConfigData(surveyId, selectedMultCrossConfigId, dispatch);
      }
      setWithData(withDataState);
    } else {
      resetData();
    }
  }, [
    chartState,
    selectedMultCrossConfigId,
    resetData,
    dispatch,
    questionId,
    surveyId,
  ]);

  // limpar os dados quando a questão for alterada
  useEffect(() => {
    resetData();
    resetPresets();
  }, [questionId, resetData, resetPresets]);

  // handle on open config (modal)
  function handleOpenConfig() {
    setShowConfig(true);
  }

  // handle on close config (modal)
  function handleCloseConfig() {
    setShowConfig(false);
    setEditableConfigId(null);
  }

  // handle on save config (modal)
  function handleSaveConfig() {
    handleCloseConfig();
    refreshConfigs();
  }

  // handle on remove config (card)
  function handleRemoveConfig(configId) {
    removeMultCrossConfig(customerId, surveyId, questionId, configId, dispatch);
    refreshConfigs();
  }

  // handle on edit config (card)
  function handleEditConfig(configId) {
    setEditableConfigId(configId);
    setShowConfig(true);
  }

  // handle configuration selection (card)
  function handleSelectedConfig(configId) {
    // Atualiza o estado do redux para armazenar o id da configuração de cruzamento atual.
    setSelectedMultCrossConfigId(configId);
  }

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  // handle change type
  function handleTypeChange(newType) {
    setMultipleCrossChartType(newType);
  }

  // handle color scheme type change
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // handle change filter value
  function handleChangeFilterValue(text) {
    setFilter(text);
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleCloseExporting() {
    setShowExporting(false);
  }

  function handleExportingCancel() {
    handleCloseExporting();
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleOpenExporting() {
    setShowExporting(true);
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        const message = 'Houve uma falha ao recuperar sua anotação, tente novamente';
        toast.error(message);
      },
      save: () => {
        const message = 'Não foi possível salvar sua anotação, tente novamente';
        toast.error(message);
      },
    };
    errorStrategy[errorType]();
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<configbtn>]}
  // menu-structure: {charttypes = {type, key, selectedIndex, onSelect, data[{value, label}]}}
  // menu-structure: {configbtn = {type, key, onclick}}
  function buildMenu() {
    // result
    const result = [];
    // create chart menu
    const types = graphTypes.get('cruzamentos-multiplos').type;
    const selectedTypeIndex = types.findIndex(
      (type) => type.value === multipleCrossChartType,
    );

    const colorSchemeTypes = graphTypes.get(
      'cruzamentos-multiplos',
    ).colorSchemeType;
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (type) => type.value === colorSchemeValue,
    );

    const chartFontTypes = graphTypes.get(
      'cruzamentos-multiplos',
    ).chartFontsType;
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (type) => type.value === fontsSizes,
    );

    const chartTypesOption = {
      type: 'charttypes',
      selectedIndex: selectedTypeIndex,
      data: types,
      onSelect: handleTypeChange,
    };

    const colorSchemeOption = {
      type: 'colorscheme',
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: 'chartfonts',
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: 'annotations',
      onClick: handleOpenAnnotations,
    };

    const exportChartOption = {
      type: 'exportChart',
      onClick: handleOpenExporting,
    };

    const configurationOption = {
      type: 'configbtn',
      onClick: handleOpenConfig,
      title: 'Configurar cruzamento...',
    };

    const options = chartData && chartData.length > 0 ? [
      chartTypesOption,
      annotationsOption,
      colorSchemeOption,
      chartFontsOption,
      exportChartOption,
      configurationOption,
    ] : [
      chartTypesOption,
      colorSchemeOption,
      chartFontsOption,
      configurationOption,
    ];
    result.push(...options.map((option, index) => ({ ...option, key: `#key${index}` })));

    return result;
  }

  function renderAnnotations() {
    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={annotationStrategyMap.multipleCross}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={selectedMultCrossConfigId}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  function renderExporting() {
    return showExporting ? (
      <ContextualPopupExporting
        onCancel={handleExportingCancel}
        types={exportingTypes}
        theme={theme}
        selectedChartType={multipleCrossChartType}
        selectedCardType={MULTIPLE_CARD}
        questionLabels={
          getQuestionTitlesByChartType(
            MULTIPLE_CARD,
            {
              pergunta_principal: questionTitle,
              profileText,
            },
          )
        }
        surveyId={surveyId}
        analyzeId={selectedMultCrossConfigId}
        customerId={customerId}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      <Card
        // minimizable={false}
        withPresets
        withEditablePreset
        withRemovablePreset
        ref={cardRef}
        cardId={cardsMap.cruzamentosMultiplos.cardId}
        cardTitle={'Cruzamentos Múltiplos'}
        cardTooltip={'Cruze todas as alternativas da pergunta selecionada com uma única alternativa de uma ou mais perguntas do questionário.'}
        questionTitle={questionTitle}
        cardTitleCompl={profileText}
        filtersTypes={null}
        contentHeight={'300px'}
        isFullScreenMode={isFullScreen}
        menu={buildMenu()}
        withData={withData}
        loading={chartState.running}
        presets={presets}
        loadingPresets={configurationsState.running}
        onSelectPreset={(items) => handleSelectedConfig(items[0].value)}
        onRemovePreset={(item) => handleRemoveConfig(item.value)}
        onEditPreset={(item) => handleEditConfig(item.value)}
        onChangeFilterValue={handleChangeFilterValue}
        onMaximize={handleFullScreenToggle}
        theme={theme}
      >
        <MultipleCrossChart
          type={multipleCrossChartType}
          data={chartData}
          colors={themeManager.calculatedChartColorArray}
          chartFontSize={fontsSizes}
          legendsFilter={filter}
          legendsFilterHandler={filterByKeywords}
          theme={theme.colors.charts}
          themeTitle={theme.title}
          updateChartColors={themeManager.updateChartColors}
          colorPickerColors={colorPickerColors}
          error={chartConfigDataError}
          chartRef={chartRef}
          cardType={MULTIPLE_CARD}
        />
      </Card>
      {/* modal */}
      {showConfig ? (
        <MultCrossConfig
          customerId={customerId}
          surveyId={surveyId}
          questionId={questionId}
          questionTitle={questionTitle}
          configId={editableConfigId}
          isOpen={showConfig}
          onClose={() => handleCloseConfig()}
          onSave={(editing) => handleSaveConfig(editing)}
        />
      ) : null}
      {renderAnnotations()}
      {renderExporting()}
    </Ui.CardContainer>
  );
}

export default MultipleCrossChartCard;
